import { useContent } from '@/hooks/useContent';
import insuranceCard from '@/static/find_insurance_id_card.gif';
import { Box, Flex, font } from '@lemonade-hq/cdk';
import { IconButton, MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { Faq } from 'contentlayer/generated';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type React from 'react';
import { useEffect, useState } from 'react';
import { animateScroll } from 'react-scroll';
import styled, { createGlobalStyle } from 'styled-components';
import { SectionParagraph } from '../shared/Content';

const GlobalStyle = createGlobalStyle`
  em {
    font-style: italic;
  }
`;

const SCROLL_FROM_TOP = 2000;

const MainHeading = styled.h1`
  ${font('main', {
    fontSize: '4.8rem',
    lineHeight: '4.8rem',
  })}
  text-align: left;
  font-family: var(--alt-font-family);
  padding: 0;
  margin-bottom: 30px;
  margin-top: 70px;
  ${MEDIA_QUERIES.mobile`
    font-size: 4.2rem;
    line-height: 4.2rem;
    word-wrap: break-word;
  `}
`;

const Heading1 = styled.h2`
  ${font('main', {
    fontSize: '4.8rem',
    fontWeight: 700,
    lineHeight: '4.8rem',
  })}
  text-align: left;
  padding: 0;
  margin-bottom: 15px;
  margin-top: 50px;
  ${MEDIA_QUERIES.mobile`
    word-wrap: break-word;
  `}
`;

const Text = styled(SectionParagraph)`
  text-align: left;
  margin-bottom: 20px;

  strong {
    font-weight: 700;
  }
`;

const Heading2 = styled.h3`
  ${font('main', { fontSize: '2.1rem', fontWeight: 700 })}
  text-align: left;
  margin: 30px 0 10px 0;
  scroll-margin-top: 100px;
`;

const Heading3 = styled.h4`
  ${font('main', { fontSize: '1.6rem', fontWeight: 700 })};
  margin-bottom: 20px;
`;

const BulletList = styled.ul`
  ${font('main', { fontSize: '1.8rem' })}
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style-type: none;
  margin-left: 20px;
  margin-bottom: 20px;

  li::before {
    font-size: 2.2rem;
    content: '•';
    display: inline-block;
    width: 1em;
    margin-left: -1em;
  }
`;

const OrderedList = styled.ol`
  ${font('main', { fontSize: '1.8rem' })}
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style-type: none;
  margin-left: 18px;
  margin-bottom: 20px;
  list-style-type: decimal;
`;

const ScrollToTop = styled(IconButton)`
  position: fixed;
  bottom: 20px;
  right: 20px;
  transform: rotate(90deg);
  border-radius: 50%;
  z-index: 9;
`;

const InsuranceCardImage: React.FC = () => {
  return (
    <Flex justifyContent="center">
      <Image src={insuranceCard} width={336} height={460} alt="Lemonade app, car insurance id card" />
    </Flex>
  );
};

const components = {
  h1: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <Heading1 {...props} />,
  h2: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <Heading2 {...props} />,
  h3: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <Heading3 {...props} />,
  p: ({ ...props }: React.HTMLAttributes<HTMLParagraphElement>) => <Text {...props} />,
  ul: ({ ...props }: React.HTMLAttributes<HTMLUListElement>) => <BulletList {...props} />,
  ol: ({ ...props }: React.HTMLAttributes<HTMLUListElement>) => <OrderedList {...props} />,
  InsuranceCardImage,
  Flex,
};

export const FaqPageContent: React.FC<{ content: Faq }> = ({ content }) => {
  const Component = useContent({ content });
  const route = useRouter();

  const [showScrollToTop, setShowScrollToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > SCROLL_FROM_TOP);
    };

    window.addEventListener('scroll', handleScroll);

    if (route.asPath.includes('#')) {
      const sanitazedPath = decodeURIComponent(route.asPath.split('#')[1]);
      const element = document.getElementById(sanitazedPath);
      if (element) {
        // smooth scroll to the element
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, [route]);

  return (
    <Box width="100%" textAlign="left" pl={[0, 10]} mt={[-30, -70]} pb={40} pt={[60, 60, 0]}>
      <GlobalStyle />
      <MainHeading>Lemonade Insurance FAQs</MainHeading>
      <Component components={components} />

      {showScrollToTop && (
        <ScrollToTop flavor="bright" circular buttonType="back" onClick={() => animateScroll.scrollToTop()}>
          back to top
        </ScrollToTop>
      )}
    </Box>
  );
};
